import type { JumpChart } from '@shared/jumpChart';
import type { JumpEngineState } from '@shared/engine/jump/state';
import {
  computeJumpLayout,
  cubeCanvasX,
  JUMP_VISIBLE_BEATS_AHEAD,
  obstacleCanvasX,
  playfieldYToCanvas,
  type JumpLayout,
} from './jumpPlayfieldGeometry';
import { JumpSceneRenderer } from './render/jumpDrawScene';
import { JumpSceneAtmosphere } from './render/jumpSceneAtmosphere';
import { JumpSpeedLines } from './render/jumpDrawSpeedLines';
import { drawJumpObstacles } from './render/jumpDrawObstacles';
import { drawJumpPlayer } from './render/jumpDrawPlayer';
import { drawJumpHud, getJumpScoreHudTarget } from './render/jumpDrawHud';
import { drawJumpOverlays } from './render/jumpDrawOverlays';
import { JumpFxSystem } from './render/jumpFx';
import { getRenderQuality, type RenderQuality } from '../rendering/renderQuality';
import {
  CUBE_PRESS_PULSE_RATE,
  CUBE_SNAP_RATE,
  CUBE_SPIN_RAD_PER_SEC,
  HIT_STOP_MAX_MS,
  HOLD_EMBER_COUNT,
  HOLD_EMBER_INTERVAL_FRAMES,
  HOLD_EMBER_INTERVAL_FRAMES_LOW,
  MILESTONE_BURST_COUNT,
  PLAYER_TRAIL_LENGTH,
  PLAYER_TRAIL_LENGTH_LOW_TIER,
  VERDICT_TEXT_X_OFFSET,
  VERDICT_TEXT_Y_OFFSET,
} from './render/jumpRenderConstants';
import type {
  JumpDeathShatter,
  JumpParticleEmission,
  JumpRenderInput,
  JumpShakeSpec,
  JumpTrailSample,
} from './render/jumpRenderTypes';

const HALF_PI = Math.PI / 2;
const TWO_PI = Math.PI * 2;

interface JumpParticleKind {
  count: number;
  /** Lifetime in seconds. */
  life: number;
  /** Fan width (radians) the burst spreads across, centred on baseAngle. */
  spread: number;
  /** Centre direction in canvas angle (PI = left/back, -PI/2 = up, +PI/2 = down). */
  baseAngle: number;
  size: number;
  speedMin: number;
  speedMax: number;
  /** Downward acceleration (px/s²) — dust settles; sparks float. */
  gravity: number;
}

/**
 * Per-type burst shape for {@link JumpRenderer.emitParticles}.
 *
 * `obstacle_pass` sprays BACKWARD-and-up so cleared-spike sparks trail behind
 * the cube rather than clouding the lane ahead. `land` kicks a low, gravity-fed
 * dust puff sideways along the ground. `jump` puffs straight down (launch
 * exhaust). `death` / `milestone` are full radial blooms.
 */
const JUMP_PARTICLE_KINDS: Record<JumpParticleEmission['type'], JumpParticleKind> = {
  obstacle_pass: {
    count: 14,
    life: 0.45,
    spread: Math.PI * 0.6,
    baseAngle: Math.PI,
    size: 2.5,
    speedMin: 110,
    speedMax: 240,
    gravity: 0,
  },
  land: {
    count: 10,
    life: 0.4,
    spread: Math.PI * 0.5,
    baseAngle: Math.PI,
    size: 2.5,
    speedMin: 60,
    speedMax: 170,
    gravity: 520,
  },
  jump: {
    count: 8,
    life: 0.32,
    spread: Math.PI * 0.7,
    baseAngle: HALF_PI,
    size: 2.2,
    speedMin: 50,
    speedMax: 150,
    gravity: 0,
  },
  death: {
    count: 36,
    life: 0.9,
    spread: TWO_PI,
    baseAngle: -HALF_PI,
    size: 3,
    speedMin: 80,
    speedMax: 220,
    gravity: 0,
  },
  milestone: {
    count: 50,
    life: 1.1,
    spread: TWO_PI,
    baseAngle: -HALF_PI,
    size: 3,
    speedMin: 80,
    speedMax: 220,
    gravity: 0,
  },
};

/**
 * Top-level Jump renderer. Owns the canvas, DPR-aware sizing, the particle
 * system, the cube-trail ring buffer, screen-shake, and the cube's tumble
 * rotation. Each `draw()` call fans work out to the small `jumpDraw*` modules:
 *
 *   1. Scene (sky gradient + parallax stars + tile terrain)
 *   2. Obstacles (spikes, saws, ceilings + grid hints)
 *   3. Particles + shockwaves (drawn BEHIND the cube)
 *   4. Cube + recent trajectory trail
 *   5. HUD (score, combo, hint)
 *   6. Overlays (floating text, score pops, countdown, death)
 *
 * Subsystems are composed via constructor injection — never inherited.
 */
export class JumpRenderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private dpr = 1;
  private viewWidth = 0;
  private viewHeight = 0;
  private lastFrameAt = performance.now();
  private reducedMotion = false;
  private quality: RenderQuality;
  private fx: JumpFxSystem;
  private scene: JumpSceneRenderer;
  private speedLines: JumpSpeedLines;
  private atmosphere = new JumpSceneAtmosphere();
  /** Playfield→canvas mapping, recomputed only on resize (constant per frame). */
  private layout: JumpLayout = computeJumpLayout(0, 0);
  /** Remaining hit-stop time (seconds). While >0, animation dt is frozen. */
  private hitStopRemaining = 0;
  /** Cube tumble angle (radians), advanced while airborne, snapped flat when grounded. */
  private cubeRotation = 0;
  /** Eased 0..1 press swell — tracks whether the button is held (incl. count-in), eased per frame. */
  private pressPulse = 0;
  /** Frame counter throttling the auto-hop hold-aura ember emission. */
  private holdEmberFrame = 0;
  private readonly trailCapacity: number;
  private readonly trailSamples: JumpTrailSample[] = [];
  private trailWriteIdx = 0;
  private trailCount = 0;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) {
      throw new Error('Failed to acquire Jump canvas 2d context');
    }
    this.ctx = ctx;
    if (typeof window !== 'undefined' && window.matchMedia) {
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
    this.quality = getRenderQuality();
    this.trailCapacity =
      this.quality.tier === 'low' ? PLAYER_TRAIL_LENGTH_LOW_TIER : PLAYER_TRAIL_LENGTH;
    this.fx = new JumpFxSystem(this.reducedMotion, this.quality);
    this.scene = new JumpSceneRenderer(this.quality);
    this.speedLines = new JumpSpeedLines(this.quality);
    this.resize();
  }

  /** Brief render-time freeze on impactful moments. Engine + audio keep running. */
  requestHitStop(ms: number): void {
    if (this.reducedMotion) {
      return;
    }
    const clamped = Math.min(ms, HIT_STOP_MAX_MS);
    this.hitStopRemaining = Math.max(this.hitStopRemaining, clamped / 1000);
  }

  resize(): void {
    const rect = this.canvas.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, this.quality.dprCap);
    const cssWidth = Math.max(1, Math.floor(rect.width));
    const cssHeight = Math.max(1, Math.floor(rect.height));
    this.canvas.width = cssWidth * dpr;
    this.canvas.height = cssHeight * dpr;
    this.dpr = dpr;
    this.viewWidth = cssWidth;
    this.viewHeight = cssHeight;
    this.layout = computeJumpLayout(cssWidth, cssHeight);
    this.scene.invalidate();
    this.atmosphere.invalidate();
  }

  getRenderDpr(): number {
    return this.dpr;
  }

  getViewSize(): { width: number; height: number } {
    return { width: this.viewWidth, height: this.viewHeight };
  }

  getScoreHudAnchor(): { x: number; y: number } {
    return getJumpScoreHudTarget();
  }

  /** Canvas-space anchor of an obstacle at its judgement beat (cube X column, cube Y). */
  getObstacleAnchor(
    _chart: JumpChart,
    state: JumpEngineState,
    obstacleBeat: number,
    currentBeat: number,
  ): { x: number; y: number } {
    const layout = this.layout;
    const visibleBeats = JUMP_VISIBLE_BEATS_AHEAD;
    return {
      x: obstacleCanvasX(layout, currentBeat, obstacleBeat, visibleBeats),
      y: playfieldYToCanvas(layout, state.cubeY),
    };
  }

  getPlayerAnchor(state: JumpEngineState): { x: number; y: number } {
    const layout = this.layout;
    return {
      x: cubeCanvasX(layout),
      y: playfieldYToCanvas(layout, state.cubeY),
    };
  }

  /**
   * Canvas anchor at the cube's BASE (bottom edge) — where landing dust + the
   * ground ripple originate. Maps the cube bottom through the layout so it stays
   * planted on the surface at any window scale (the centre anchor sits half a
   * cube too high for a surface-hugging effect).
   */
  getCubeBaseAnchor(chart: JumpChart, state: JumpEngineState): { x: number; y: number } {
    const layout = this.layout;
    return {
      x: cubeCanvasX(layout),
      y: playfieldYToCanvas(layout, state.cubeY + chart.playerSize / 2),
    };
  }

  /** Anchor for verdict floating text — above the cube, clear of the approach lane. */
  getVerdictTextAnchor(state: JumpEngineState): { x: number; y: number } {
    const layout = this.layout;
    return {
      x: cubeCanvasX(layout) + VERDICT_TEXT_X_OFFSET,
      y: playfieldYToCanvas(layout, state.cubeY) - VERDICT_TEXT_Y_OFFSET,
    };
  }

  emitParticles(emission: JumpParticleEmission): void {
    if (this.reducedMotion) {
      return;
    }
    const kind = JUMP_PARTICLE_KINDS[emission.type];
    let count = kind.count;
    if (emission.type === 'milestone' && emission.tier !== undefined) {
      const tierIndex = Math.max(0, Math.min(emission.tier - 1, MILESTONE_BURST_COUNT.length - 1));
      count = MILESTONE_BURST_COUNT[tierIndex];
    }
    if (emission.strength !== undefined) {
      count = Math.max(1, Math.round(count * emission.strength));
    }
    this.fx.emit({
      x: emission.x,
      y: emission.y,
      colour: emission.colour,
      count,
      life: kind.life,
      size: kind.size,
      angleStart: kind.baseAngle - kind.spread / 2,
      angleEnd: kind.baseAngle + kind.spread / 2,
      speedMin: kind.speedMin,
      speedMax: kind.speedMax,
      gravity: kind.gravity,
    });
  }

  /**
   * Auto-hop hold aura: while the button is held the cube sheds a faint stream
   * of accent embers (drawn behind the cube) so holding reads as charged rather
   * than passive. Throttled per-frame, skipped under reduced motion + hit-stop;
   * the FX system's particle cap bounds it on dense frames.
   */
  private emitHoldAura(input: JumpRenderInput, layout: JumpLayout): void {
    if (!input.pressing || !input.state.alive || this.reducedMotion || this.hitStopRemaining > 0) {
      this.holdEmberFrame = 0;
      return;
    }
    this.holdEmberFrame += 1;
    const interval =
      this.quality.tier === 'low' ? HOLD_EMBER_INTERVAL_FRAMES_LOW : HOLD_EMBER_INTERVAL_FRAMES;
    if (this.holdEmberFrame % interval !== 0) {
      return;
    }
    const x = cubeCanvasX(layout);
    const y = playfieldYToCanvas(layout, input.state.cubeY);
    const size = input.chart.playerSize * layout.scaleY;
    this.fx.emit({
      x,
      y: y + size * 0.4,
      colour: input.chart.palette.player,
      count: HOLD_EMBER_COUNT,
      life: 0.34,
      size: 2,
      angleStart: -Math.PI * 0.62,
      angleEnd: -Math.PI * 0.38,
      speedMin: 40,
      speedMax: 110,
      gravity: 0,
    });
  }

  /** Drop all trail samples — called on loop wrap so the trail doesn't span loops. */
  clearTrail(): void {
    this.trailSamples.length = 0;
    this.trailWriteIdx = 0;
    this.trailCount = 0;
  }

  resetAll(): void {
    this.clearTrail();
    this.fx.reset();
    this.hitStopRemaining = 0;
    this.cubeRotation = 0;
    this.pressPulse = 0;
    this.holdEmberFrame = 0;
  }

  /**
   * Record the cube's centre Y for this frame into the trail ring buffer. Once
   * the buffer is full it overwrites in place (mutating the slot) so steady-state
   * playback allocates nothing.
   */
  pushTrailSample(beat: number, cubeY: number): void {
    if (this.trailCount < this.trailCapacity) {
      this.trailSamples.push({ beat, cubeY });
      this.trailCount += 1;
      return;
    }
    const slot = this.trailSamples[this.trailWriteIdx];
    slot.beat = beat;
    slot.cubeY = cubeY;
    this.trailWriteIdx = (this.trailWriteIdx + 1) % this.trailCapacity;
  }

  /**
   * Advance the cube's tumble. Airborne, it spins forward at a constant rate
   * (a quarter-turn per ~0.22s reads as a Geometry-Dash tumble); grounded, it
   * eases to the nearest flat face so it always lands square.
   */
  private updateCubeRotation(grounded: boolean, dt: number): void {
    if (!grounded) {
      this.cubeRotation = (this.cubeRotation + CUBE_SPIN_RAD_PER_SEC * dt) % TWO_PI;
      return;
    }
    const nearestFlat = Math.round(this.cubeRotation / HALF_PI) * HALF_PI;
    const k = 1 - Math.exp(-CUBE_SNAP_RATE * dt);
    this.cubeRotation += (nearestFlat - this.cubeRotation) * k;
    if (Math.abs(nearestFlat - this.cubeRotation) < 1e-3) {
      this.cubeRotation = ((nearestFlat % TWO_PI) + TWO_PI) % TWO_PI;
    }
  }

  /**
   * Ease the press swell toward 1 while the button is held, 0 otherwise, so a
   * tap reads as a quick squeeze. Suppressed under reduced motion (eased to 0)
   * alongside the cube's other deformation. Runs every frame — including the
   * count-in — so spam-clicking before the run starts still responds.
   */
  private updatePressPulse(pressing: boolean, dt: number): void {
    const target = this.reducedMotion ? 0 : pressing ? 1 : 0;
    const k = 1 - Math.exp(-CUBE_PRESS_PULSE_RATE * dt);
    this.pressPulse += (target - this.pressPulse) * k;
  }

  draw(input: JumpRenderInput): void {
    if (input.width <= 0 || input.height <= 0) {
      return;
    }
    if (
      Math.floor(input.width) !== this.viewWidth ||
      Math.floor(input.height) !== this.viewHeight
    ) {
      this.resize();
    }
    const now = performance.now();
    const wallDt = Math.min(0.05, (now - this.lastFrameAt) / 1000);
    this.lastFrameAt = now;
    let animDt: number;
    if (this.hitStopRemaining > 0) {
      this.hitStopRemaining = Math.max(0, this.hitStopRemaining - wallDt);
      animDt = 0;
    } else {
      animDt = wallDt;
    }

    for (const emission of input.particleEmissions) {
      this.emitParticles(emission);
    }
    input.particleEmissions.length = 0;
    this.fx.consumeShockwaveSpecs(input.shockwaveSpecs);
    input.shockwaveSpecs.length = 0;

    this.pushTrailSample(input.beat, input.state.cubeY);
    this.updateCubeRotation(input.state.grounded, animDt);
    this.updatePressPulse(input.pressing, animDt);

    const layout = this.layout;
    const visibleBeats = JUMP_VISIBLE_BEATS_AHEAD;

    // Cube deformation is motion, so it's suppressed under reduced motion like
    // every other FX here. Airborne stretch reads the cube's vertical speed from
    // the closed-form arc (render-only; the engine anchors every segment at/
    // before the current beat, so the elapsed beats are non-negative) and
    // normalises it by the launch speed. Never fed back into the engine.
    const st = input.state;
    const edgeImpulse = this.reducedMotion ? 0 : input.edgeImpulse;
    const cubeVy = st.anchorVy + input.chart.gravity * (st.beat - st.anchorBeat);
    const airStretch =
      st.grounded || this.reducedMotion
        ? 0
        : Math.min(1, Math.abs(cubeVy) / input.chart.jumpVelocity);

    // On death the cube is drawn as a frozen shard burst by the overlay pass;
    // drawJumpPlayer skips its body so the two never double up.
    const deathShatter: JumpDeathShatter | null = st.alive
      ? null
      : {
          x: cubeCanvasX(layout),
          y: playfieldYToCanvas(layout, st.cubeY),
          size: input.chart.playerSize * layout.scaleY,
          rotation: this.cubeRotation,
          colour: input.chart.palette.player,
        };

    const ctx = this.ctx;
    const shakeAmount = input.shake.amount;
    const biasY = input.shake.biasY ?? 0;
    const shakeX =
      this.reducedMotion || shakeAmount < 0.01 ? 0 : (Math.random() - 0.5) * shakeAmount * 2;
    const shakeY =
      this.reducedMotion || shakeAmount < 0.01
        ? 0
        : (Math.random() - 0.5 + biasY * 0.6) * shakeAmount * 2;

    ctx.save();
    ctx.scale(this.dpr, this.dpr);
    ctx.translate(shakeX, shakeY);

    this.scene.draw(
      ctx,
      layout,
      input.chart,
      input.beat,
      visibleBeats,
      this.viewWidth,
      this.viewHeight,
      input.beatPulse,
      input.comboEnergy,
    );
    // Combo bloom sits behind the obstacles + cube as an ambient energy wash,
    // anchored on the cube itself so it tracks the player up onto platforms and
    // through jumps (terrain height now varies, so there's no single ground line).
    this.atmosphere.drawComboBloom(
      ctx,
      this.viewWidth,
      this.viewHeight,
      cubeCanvasX(layout),
      playfieldYToCanvas(layout, input.state.cubeY),
      input.comboEnergy,
      input.chart.palette.player,
      input.beatPulse,
      this.reducedMotion,
      this.quality,
    );
    // "In the zone" speed lines rush past behind the obstacles once a streak
    // pushes combo energy up — additive + faint so hazards stay readable.
    this.speedLines.draw(
      ctx,
      this.viewWidth,
      this.viewHeight,
      input.beat,
      input.comboEnergy,
      input.chart.palette.player,
      this.reducedMotion,
    );
    drawJumpObstacles(
      ctx,
      layout,
      input.chart,
      input.beat,
      visibleBeats,
      this.quality.chromeShadows,
      !this.reducedMotion,
    );
    this.emitHoldAura(input, layout);
    this.fx.step(animDt);
    this.fx.draw(ctx, this.quality.chromeShadows);

    drawJumpPlayer(
      ctx,
      layout,
      input.chart,
      input.state,
      this.cubeRotation,
      {
        samples: this.trailSamples,
        startIdx: this.trailCount < this.trailCapacity ? 0 : this.trailWriteIdx,
        count: this.trailCount,
        capacity: this.trailCount < this.trailCapacity ? this.trailCount : this.trailCapacity,
      },
      input.beatPulse,
      input.comboEnergy,
      edgeImpulse,
      airStretch,
      this.pressPulse,
      this.quality.chromeShadows,
    );

    // Vignette frames the gameplay but draws beneath the HUD/overlays so score
    // + verdict text stay crisp.
    this.atmosphere.drawVignette(
      ctx,
      this.viewWidth,
      this.viewHeight,
      input.comboEnergy,
      this.quality,
    );

    drawJumpHud(
      ctx,
      input.chart,
      input.state,
      input.displayedScore,
      this.viewHeight,
      input.keyLabel,
    );

    drawJumpOverlays(
      ctx,
      input.chart,
      input.state,
      input.floatingTexts,
      input.scorePops,
      input.flashes,
      input.countdown,
      this.viewWidth,
      this.viewHeight,
      animDt,
      this.quality.chromeShadows,
      deathShatter,
    );

    ctx.restore();
  }
}

/** Construct an initial empty shake spec to seed a session field. */
export function createEmptyJumpShake(): JumpShakeSpec {
  return { amount: 0, decay: 0 };
}
